/**
 * Administration nav grouping (Finance, Procurement, Inventory, Catering).
 * HR stays at root level.
 */

export const ADMIN_CHILD_MENU_IDS = ['finance', 'supply-chain', 'inventory', 'catering'];

export function isAdminChildMenuId(menuId) {
  return ADMIN_CHILD_MENU_IDS.includes(menuId);
}

export function mapActiveMenuIdForGrouping(menuId) {
  if (!menuId) return menuId;
  return isAdminChildMenuId(menuId) ? 'administration' : menuId;
}

export function resolveMobileLevel1MenuId(menuId) {
  if (!menuId) return null;
  return mapActiveMenuIdForGrouping(menuId);
}

export const FINANCE_CHILD_ROUTES = [
  '/finance/petty-cash',
  '/finance/petty-cash/new',
  '/finance/petty-cash/funds',
  '/finance/petty-cash/reports',
  '/finance/petty-cash/reports/monthly',
  '/finance/corporate-expenses',
  '/finance/corporate-expenses/new',
  '/finance/company-assets',
  '/finance/company-assets/new',
  '/finance/coa',
  '/finance/exchange-rates',
];

export const INVENTORY_CHILD_ROUTES = [
  '/inventory/dashboard',
  '/inventory/new',
  '/inventory/kits',
  '/inventory/parameters',
  '/inventory/vendors',
];

export const PROCUREMENT_CHILD_ROUTES = [
  '/supply-chain/requisitions',
  '/supply-chain/purchase-orders',
  '/supply-chain/approval-inbox',
  '/supply-chain/vendors',
];

export const CATERING_CHILD_ROUTES = [
  '/catering/estimator',
  '/catering/food-items',
  '/catering/rations',
  '/catering/estimates',
];

function showAdministrationFinance({ showCompanyMenu, isAdmin, hasMenuAccessOrChild }) {
  return showCompanyMenu('finance') && isAdmin && hasMenuAccessOrChild('/finance', FINANCE_CHILD_ROUTES);
}

function showAdministrationProcurement({ showCompanyMenu, hasMenuAccessOrChild }) {
  return showCompanyMenu('supply-chain')
    && hasMenuAccessOrChild('/supply-chain', PROCUREMENT_CHILD_ROUTES);
}

function showAdministrationCatering({ showCompanyMenu, hasMenuAccessOrChild }) {
  return showCompanyMenu('catering')
    && hasMenuAccessOrChild('/catering', CATERING_CHILD_ROUTES);
}

function showAdministrationInventoryDesktop({ showCompanyMenu, hasMenuAccess, hasMenuAccessOrChild }) {
  if (!showCompanyMenu('inventory')) return false;
  const showFieldOnly =
    (hasMenuAccess('/inventory/mobile') || hasMenuAccess('/inventory/new'))
    && !hasMenuAccess('/inventory');
  return showFieldOnly || hasMenuAccessOrChild('/inventory', INVENTORY_CHILD_ROUTES);
}

function showAdministrationInventoryMetro({
  showCompanyMenu,
  hasInventoryAccess,
  showGenericInventoryMetroTile,
}) {
  return showCompanyMenu('inventory') && hasInventoryAccess && showGenericInventoryMetroTile;
}

/**
 * Whether the Administration sidebar group should render.
 */
export function hasAdministrationDesktopAccess(ctx) {
  return (
    showAdministrationFinance(ctx)
    || showAdministrationProcurement(ctx)
    || showAdministrationInventoryDesktop(ctx)
    || showAdministrationCatering(ctx)
  );
}

/**
 * Whether the Administration metro tile should render.
 */
export function hasAdministrationMetroAccess(ctx) {
  return (
    showAdministrationFinance(ctx)
    || showAdministrationProcurement(ctx)
    || showAdministrationInventoryMetro(ctx)
    || showAdministrationCatering(ctx)
  );
}

export function buildAdministrationMetroSubSections(ctx) {
  const sections = [];
  if (showAdministrationFinance(ctx)) {
    sections.push({ id: 'finance', title: 'Finance' });
  }
  if (showAdministrationProcurement(ctx)) {
    sections.push({ id: 'supply-chain', title: 'Procurement' });
  }
  if (showAdministrationInventoryMetro(ctx)) {
    sections.push({ id: 'inventory', title: 'Inventory' });
  }
  if (showAdministrationCatering(ctx)) {
    sections.push({ id: 'catering', title: 'Catering' });
  }
  return sections;
}

/** Nested sidebar groups under Administration (desktop). Reserved for future column submenus. */
export const ADMIN_NESTED_GROUPS = {};

export function getAdminSidebarExpansionsForRoute(routeMenuId) {
  if (!routeMenuId) return null;
  const group = Object.values(ADMIN_NESTED_GROUPS).find((entry) => entry.routeMenuId === routeMenuId);
  if (!group) return null;
  return {
    [group.parentId]: true,
    [group.groupId]: true,
  };
}

export function isFinanceRoute(pathname, search = '') {
  const full = `${pathname}${search}`;
  return full.startsWith('/finance/');
}

function pickFirstAccessibleRoute(hasMenuAccess, routes) {
  return routes.find((route) => hasMenuAccess(route)) || routes[0];
}

function resolveInventoryLandingPath(hasMenuAccess) {
  const showFieldOnly =
    (hasMenuAccess('/inventory/mobile') || hasMenuAccess('/inventory/new'))
    && !hasMenuAccess('/inventory');
  if (showFieldOnly) {
    return hasMenuAccess('/inventory/mobile') ? '/inventory/mobile' : '/inventory/new';
  }
  if (hasMenuAccess('/inventory/dashboard') || hasMenuAccess('/inventory')) {
    return '/inventory';
  }
  return pickFirstAccessibleRoute(hasMenuAccess, INVENTORY_CHILD_ROUTES);
}

/** Module cards for /administration landing (Phase 2 — not a DB menu yet). */
export function buildAdministrationLandingModules(ctx) {
  const { hasMenuAccess } = ctx;
  const modules = [];

  if (showAdministrationFinance(ctx)) {
    modules.push({
      id: 'finance',
      title: 'Finance',
      description: 'Petty cash, corporate expenses, chart of accounts, and exchange rates.',
      icon: 'bi-currency-exchange',
      path: pickFirstAccessibleRoute(hasMenuAccess, FINANCE_CHILD_ROUTES),
    });
  }
  if (showAdministrationProcurement(ctx)) {
    modules.push({
      id: 'supply-chain',
      title: 'Procurement',
      description: 'Requisitions, purchase orders, approval inbox, and vendor management.',
      icon: 'bi-clipboard-check',
      path: pickFirstAccessibleRoute(hasMenuAccess, PROCUREMENT_CHILD_ROUTES),
    });
  }
  if (showAdministrationInventoryDesktop(ctx)) {
    modules.push({
      id: 'inventory',
      title: 'Inventory',
      description: 'Asset tracking, equipment kits, field entry, and inventory configuration.',
      icon: 'bi-box-seam',
      path: resolveInventoryLandingPath(hasMenuAccess),
    });
  }
  if (showAdministrationCatering(ctx)) {
    modules.push({
      id: 'catering',
      title: 'Catering',
      description: 'Food catalog, ration templates, and camp catering cost estimates.',
      icon: 'bi-egg-fried',
      path: pickFirstAccessibleRoute(hasMenuAccess, CATERING_CHILD_ROUTES),
    });
  }

  return modules;
}
